import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line import/no-unresolved
import ESIInclude from '@JohnLewisPartnership/jl-esi-components';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import FilterSuggestions from '../components/filter-suggestions/FilterSuggestions';
import GridRow from '../components/grid/GridRow';
import GridColumn from '../components/grid/GridColumn';
import { Banner } from '../components/banners/Banner';
import Header from '../components/header/Header';
import SubCopy from '../components/header/sub-copy/SubCopy';
import { CorrectedSearchTerm } from '../components/header/corrected-search-term/CorrectedSearchTerm';
import ProductListing from '../components/product-listing/ProductListing';
import BackToTop from '../components/back-to-top/BackToTop';
import styles from './PLP.module.scss';
import { storageCanBeSet } from '../utils/storage';
import { getAuthUserData } from '../utils/auth';
import constants from '../utils/constants';
import { useErrorContext } from '../context/ErrorContext';
import { fetchQuickView } from '../api/quick-view/index';
import { useExperiencesContext } from '../components/experiences/Experiences';
import { useBannerContext } from '../context/BannerContext';
import { useCompareContext } from '../context/CompareContext';
import { useFacetContext } from '../context/FacetContext';
import { useSearchContext } from '../context/SearchContext';
import { useBreadcrumbContext } from '../context/BreadcrumbContext';
import { useApplicationSettingsContext } from '../context/ApplicationSettingsContext';
import { useProductContext } from '../context/ProductContext';
import { usePageLoadReady } from '../hooks/usePageLoadReady';
import { dispatchCitrusAnalytics } from '../components/analytics/analyticsEvents';
import {
  BrandBanner,
  getBrandBanner,
} from '../components/brand-banner/BrandBanner';
import { AlternativeSearchTerms } from '../components/header/did-you-mean/AlternativeSearchTerms';
import { sortOptions } from '../components/sort/options';
import { getAriaTextIfPriceRange } from '../utils/index';
import { fetchOnlineStockDataForQvCallback } from '../api/online/onlineStockForQvCallback';
import { CitrusBanner } from '../components/banners/CitrusBanner';
import { LeftNavFacets } from '../components/facets/LeftNavFacets';

const {
  COMPARE_LOCAL_STORAGE_KEY,
  STORAGE_TYPES: { LOCAL_STORAGE },
} = constants;

const Compare = dynamic(
  () =>
    import(
      /* webpackChunkName: "compareComponent" */
      /* webpackPreload: true */
      '../components/compare/Compare'
    ),
  { ssr: false }
);

const importEnergyDrawer = () =>
  import(
    /* webpackChunkName: "energyDrawerComponent" */
    '../components/energy-drawer/EnergyDrawer'
  );

const importFilterDrawer = () =>
  import(
    /* webpackChunkName: "filterDrawerComponent" */
    '../components/filter-drawer/FilterDrawer'
  );

const importQuickAdd = () =>
  import(
    /* webpackChunkName: "quickAddComponent" */
    '@JohnLewisPartnership/jl-design-system/components/quick-add-to-basket/QuickAdd'
  );

const importQuickView = () =>
  import(
    /* webpackChunkName: "quickViewContainerComponent" */
    '@JohnLewisPartnership/jl-design-system/components/quick-view/QuickViewContainer'
  );

const FilterDrawer = dynamic(importFilterDrawer, { ssr: false });

const EnergyDrawer = dynamic(importEnergyDrawer, { ssr: false });

const M2MProductSelector = dynamic(
  () =>
    import(
      /* webpackChunkName: "m2mProductSelectorComponent" */
      '../components/m2m-product-selector/M2MProductSelector'
    ),
  { ssr: false }
);

const QuickAdd = dynamic(importQuickAdd, {
  ssr: false,
});

const QuickViewContainer = dynamic(importQuickView, {
  ssr: false,
});

const SocialProofMessagesQuickView = memo(
  ({ html, productId, brand, availableToOrder }) => {
    const wrapper = useRef(null);
    const container = document.createElement('div');

    if (html) {
      container.insertAdjacentHTML(
        'afterbegin',
        html
          .replace(/_productId_/g, productId)
          .replace(/_brand_/g, brand)
          .replace(/_availableToOrder_/g, availableToOrder)
      );
    }

    const scripts = container.querySelectorAll(
      "script[src*='social-proof-messaging']"
    );

    useEffect(() => {
      scripts.forEach((script) => {
        const validScript = document.createElement('script');
        validScript.setAttribute('type', 'text/javascript');
        validScript.setAttribute('defer', true);
        validScript.setAttribute('src', script.getAttribute('src'));

        wrapper.current?.appendChild(validScript);
        script.parentNode.removeChild(script);
      });
    }, []);

    return (
      <div
        ref={wrapper}
        dangerouslySetInnerHTML={{ __html: container.innerHTML }}
      ></div>
    );
  }
);
SocialProofMessagesQuickView.displayName = 'SocialProofMessagesQuickView';

export const usePlpViewData = () => {
  const { totalNumberOfResults } = useProductContext();
  const compare = useCompareContext();
  const router = useRouter();

  const { applied, isPrimaryFacetInDiscoResponse, primaryFacet } =
    useFacetContext();

  const {
    applicationPrefix,
    basketBFFService,
    quickViewSocialProofAppId,
    socialProofESIService,
  } = useApplicationSettingsContext();

  const {
    correctedSearchTerm = undefined,
    alternativeSearchSuggestions,
    term,
    correctionType,
    sortBy,
  } = useSearchContext();

  const {
    isHybridBannerFeatureFlagOn,
    isFilterSuggestionsFlagOn,
    isSocialProofingQuickViewOn,
    isNowWasFlagOn,
  } = useExperiencesContext();

  const { hasFacetsError = false, hasProductListError = false } =
    useErrorContext();

  return {
    totalNumberOfResults,
    compare,
    router,
    applicationSettings: {
      applicationPrefix,
      basketBFFService,
      quickViewSocialProofAppId,
      socialProofESIService,
    },
    experience: {
      isHybridBannerFeatureFlagOn,
      isFilterSuggestionsFlagOn,
      isSocialProofingQuickViewOn,
      isNowWasFlagOn,
    },
    searchContext: {
      correctedSearchTerm,
      alternativeSearchSuggestions,
      term,
      correctionType,
      sortBy,
    },
    errorContext: {
      hasFacetsError,
      hasProductListError,
    },
    facetContext: {
      applied,
      isPrimaryFacetInDiscoResponse,
      primaryFacet,
    },
  };
};

export const PlpView = (props) => {
  const { heading, subHeading, cookies } = props;

  const {
    totalNumberOfResults,
    compare,
    router,
    applicationSettings,
    searchContext,
    experience,
    errorContext,
    facetContext,
  } = usePlpViewData();

  const {
    applicationPrefix,
    basketBFFService,
    quickViewSocialProofAppId,
    socialProofESIService,
  } = applicationSettings;
  const EMAIL_ME_PATH = `${applicationPrefix}/api/quick-view-email-me`;

  const { type, page = '1', listHead } = router.query;
  const isSearch = type === 'search' || type === 's';

  const {
    correctedSearchTerm = undefined,
    alternativeSearchSuggestions,
    term,
    correctionType,
    sortBy,
  } = searchContext;

  const {
    isHybridBannerFeatureFlagOn,
    isFilterSuggestionsFlagOn,
    isSocialProofingQuickViewOn,
    isNowWasFlagOn,
  } = experience;

  const { hasFacetsError = false, hasProductListError = false } = errorContext;

  const [displayFilterDrawer, setDisplayFilterDrawer] = useState(false);
  const [displayM2MProductSelector, setDisplayM2MProductSelector] =
    useState(false);
  const [displayQuickAdd, setDisplayQuickAdd] = useState(false);
  const [isHeaderPeeping, setIsHeaderPeeping] = useState(false);
  const [quickAddData, setQuickAddData] = useState(null);
  const [quickAddError, setQuickAddError] = useState(null);
  const [quickViewData, setQuickViewData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    product: {},
    event: {},
  });
  const [renderFilterDrawer, setRenderFilterDrawer] = useState(false);
  const [renderSkipFilters, setRenderSkipFilters] = useState(true);
  const [m2mProductSelectorDetails, setM2MProductSelectorDetails] = useState({
    targetHref: null,
  });
  const [
    filterDrawerOpenedByPrimaryFacet,
    setFilterDrawerOpenedByPrimaryFacet,
  ] = useState(false);
  const [socialProofMessagesSkeleton, setSocialProofMessagesSkeleton] =
    useState(null);

  const sortByLabel = sortOptions.find(
    (sortOption) => sortOption.value === sortBy
  )?.label;

  const { applied, isPrimaryFacetInDiscoResponse, primaryFacet } = facetContext;

  const individualFilters = applied?.filterCombination
    ? applied.filterCombination.split('|')
    : [];
  let screenReaderFilters =
    individualFilters.length === 0
      ? 'No filters are applied'
      : 'Your applied filters are, ';

  individualFilters.forEach((filter) => {
    const [filterName, filterValue] = filter.split(':');
    const filterValueToRead =
      getAriaTextIfPriceRange(filterValue) || filterValue;
    screenReaderFilters += `${filterName} ${filterValueToRead}, `;
  });

  const fetchQuickViewData = useCallback(fetchQuickView, []);

  const fetchSocialProofMessagesComponentForQuickViewSkeleton = ({
    productId,
    brand,
    availableToOrder,
  }) => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    return (
      <SocialProofMessagesQuickView
        html={socialProofMessagesSkeleton}
        productId={productId}
        brand={brand}
        availableToOrder={availableToOrder}
      />
    );
  };

  const fetchOnlineStockForSkusForSelectedColour = async (skuIds) => {
    const stockInfo = await fetchOnlineStockDataForQvCallback(skuIds);

    return stockInfo;
  };

  const updateCitrusAdAnalyticsData = (adId, productId) => {
    const analyticsData = { product: {}, event: {} };
    if (adId) {
      analyticsData.product.citrusAdId = adId;
      dispatchCitrusAnalytics(productId, adId);
    }
    setAnalyticsData(analyticsData);
  };

  const getSkuIdsForSelectedColour = (skus, colour) =>
    skus.filter((sku) => sku.colour === colour).map(({ id }) => id); // Returns an array of skuIds matching the specified colour

  const getSkusWithStockForColour = async (skus, colour) => {
    const skuIdsForSelectedColour = getSkuIdsForSelectedColour(skus, colour);

    const stockInfo = await fetchOnlineStockDataForQvCallback(
      skuIdsForSelectedColour
    );

    return skus.map((currentSku) => {
      if (!skuIdsForSelectedColour.includes(currentSku.id)) {
        return {
          ...currentSku,
        };
      } else {
        const matchedStockValue = stockInfo.stocks.find(
          ({ skuId }) => skuId === currentSku.id
        );
        return {
          ...currentSku,
          stockLevel:
            matchedStockValue && matchedStockValue.status
              ? matchedStockValue.status
              : 0,
          preorderMessage:
            matchedStockValue && matchedStockValue.preOrderMessage
              ? matchedStockValue.preOrderMessage
              : '',
        };
      }
    });
  };

  const handleQuickAddColourChange = async (colour) => {
    const {
      data: { skus = [] },
    } = quickAddData;

    const skusWithStock = await getSkusWithStockForColour(skus, colour);

    setQuickAddData({
      ...quickAddData,
      data: {
        ...quickAddData.data,
        skus: skusWithStock,
      },
    });
  };

  const handleQuickAddDisplay = useCallback(
    async (id, productUrl, swatchOrder, preselectedColour, adId) => {
      setDisplayQuickAdd(true);
      updateCitrusAdAnalyticsData(adId, id);

      try {
        const response = await fetchQuickViewData(id);
        const { skus = [], colours = [], isPartialStockInfo } = response;
        let skusWithStock;

        if (isPartialStockInfo && colours.length > 0) {
          skusWithStock = await getSkusWithStockForColour(
            skus,
            preselectedColour || colours[0]
          );
        }

        const data = skusWithStock
          ? { ...response, skus: skusWithStock }
          : { ...response };

        setQuickAddData({
          data,
          swatchOrder,
          preselectedColour,
        });
      } catch (err) {
        setQuickAddError({
          productUrl,
          retry: () => {
            setQuickAddError(null);
            handleQuickAddDisplay(id, productUrl);
          },
        });
      }
      return Promise.resolve();
    },
    []
  );

  const handleQuickAddClose = () => {
    setQuickAddData(null);
    setDisplayQuickAdd(false);
    setQuickAddError(null);
  };

  const handleFilterDrawerClick = useCallback(() => {
    setRenderFilterDrawer(true);
    setDisplayFilterDrawer(true);
    setFilterDrawerOpenedByPrimaryFacet(false);
  }, []);

  const handlePrimaryFacetClick = () => {
    setRenderFilterDrawer(true);
    setDisplayFilterDrawer(true);
    setFilterDrawerOpenedByPrimaryFacet(true);
  };

  const handleFilterDrawerOpen = () => {
    const primaryFacetAccordionName = `accordion-summary-${primaryFacet.name
      .toLowerCase()
      .replace(/ /g, '-')}`;

    const primaryFacetElement = document.getElementById(
      primaryFacetAccordionName
    );

    if (primaryFacetElement) {
      primaryFacetElement.scrollIntoView();
    }
  };

  const handleFilterDrawerClose = useCallback(() => {
    setDisplayFilterDrawer(false);
  }, []);

  const onFetchQuickViewData = useCallback(
    (
      id,
      selectedProductUrl,
      swatchOrder,
      selectedSwatch,
      adId,
      shouldScrollToPromoMsgs = false
    ) =>
      new Promise((resolve) => {
        updateCitrusAdAnalyticsData(adId, id);
        setQuickViewData({
          id,
          selectedProductUrl,
          swatchOrder,
          preselectedColour: selectedSwatch,
          onClose: resolve,
          shouldScrollToPromoMsgs,
        });
      }),
    []
  );

  const onCloseQuickView = () => {
    quickViewData.onClose();
    return setQuickViewData(null);
  };

  const handleM2MProductSelectorClick = useCallback((targetHref) => {
    setM2MProductSelectorDetails({ targetHref });
    setDisplayM2MProductSelector(true);
  }, []);

  const onCloseM2MProductSelector = () => {
    setDisplayM2MProductSelector(false);
    setM2MProductSelectorDetails({ targetHref: null });
  };

  const handleBackButtonClick = () => {
    setQuickViewData(null);
  };

  const fetchEmailOutOfStock = async ({
    email,
    skuId,
    productId,
    customerNotifiableEvents = {},
  }) => {
    const loggedInUserData = await getAuthUserData();

    return fetch(EMAIL_ME_PATH, {
      method: 'POST',
      body: JSON.stringify({
        productId,
        skuId,
        email,
        customerNotifiableEvents,
        customerId: loggedInUserData?.customerId,
      }),
    });
  };

  usePageLoadReady();

  const toggleFilterDrawer = () => {
    if (window.innerWidth >= constants.MOBILE_MAX_WIDTH) {
      handleFilterDrawerClose();
      setRenderSkipFilters(true);
    } else {
      setRenderSkipFilters(false);
    }
  };

  useEffect(() => {
    window.addEventListener(
      'jl-events.browser.mobileHeaderPeepUpdated',
      ({ detail: { peeped = false } }) => {
        setIsHeaderPeeping(peeped);
      }
    );

    const importQuickAction = () => {
      if (window.innerWidth < 768) {
        importQuickAdd();
      } else {
        importQuickView();
      }
    };

    setRenderSkipFilters(window.innerWidth >= constants.MOBILE_MAX_WIDTH);

    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        importQuickAction();
        importFilterDrawer();
        importEnergyDrawer();
      });
    } else {
      importQuickAction();
      importFilterDrawer();
      importEnergyDrawer();
    }

    window.addEventListener('resize', toggleFilterDrawer);

    if (isSocialProofingQuickViewOn) {
      const socialProofUrl = `${socialProofESIService}?${new URLSearchParams({
        productId: '_productId_',
        appId: quickViewSocialProofAppId,
        brand: '_brand_',
        availableToOrder: '_availableToOrder_',
        withClose: 'false',
      }).toString()}`;

      fetch(socialProofUrl)
        .then((response) => response.text())
        .then(setSocialProofMessagesSkeleton)
        .catch((err) => {
          console.log(`PLP: Error fetching QV social proof skeleton`, err);
        });
    }

    return () => {
      window.removeEventListener(
        'jl-events.browser.mobileHeaderPeepUpdated',
        () => {}
      );
      window.removeEventListener('resize', toggleFilterDrawer);
    };
  }, []);

  const storageType = LOCAL_STORAGE;
  const storageKey = COMPARE_LOCAL_STORAGE_KEY;

  compare.setAvailability(
    storageCanBeSet({
      storageType,
      storageKey,
      cookies: cookies?.current,
    })
  );

  if (totalNumberOfResults === 0) {
    return (
      <React.Fragment>
        <GridRow id="js-plp-header" className={styles.plp__header}>
          <GridColumn key="search-header">
            <Header
              heading={heading}
              subHeading={subHeading}
              showNumberOfResults={false}
              pageType={type}
            />
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn>
            <SubCopy />
          </GridColumn>
        </GridRow>

        <ESIInclude
          src="/content-renderer/no-results?includeCoreCss=false&includeCoreJS=false&includePolyfills=false"
          id="plp-no-results"
        />
      </React.Fragment>
    );
  }

  let screenReaderUpdates =
    totalNumberOfResults !== 1
      ? `There are ${totalNumberOfResults} results, `
      : `There is 1 result, `;
  if (sortByLabel) {
    screenReaderUpdates += `Results are sorted by ${sortByLabel}, `;
  }
  if (screenReaderFilters) {
    screenReaderUpdates += screenReaderFilters;
  }

  return (
    <React.Fragment>
      <GridRow id="js-plp-header" className={styles.plp__header}>
        <GridColumn key="search-header">
          <Header
            heading={isSearch && listHead ? listHead : heading}
            subHeading={subHeading}
            showNumberOfResults
            totalNumberOfResults={
              correctedSearchTerm ? 0 : totalNumberOfResults
            }
            alternativeSearchSuggestions={
              alternativeSearchSuggestions &&
              alternativeSearchSuggestions.length > 0 ? (
                <AlternativeSearchTerms
                  alternativeSearchSuggestions={alternativeSearchSuggestions}
                />
              ) : null
            }
            correctedSearchTerm={
              isSearch ? (
                <CorrectedSearchTerm
                  correctedSearchTerm={correctedSearchTerm}
                  totalNumberOfResults={totalNumberOfResults}
                  originalSearchTerm={term}
                  correctionType={correctionType}
                />
              ) : null
            }
          />
          {page === '1' && (
            <Banner bannerId="seoTopBannerId" className="seobanners-top" />
          )}
          {screenReaderUpdates && (
            <span
              id="screen-reader-updates"
              className={styles.visuallyHidden}
              aria-live="assertive"
            >
              {screenReaderUpdates}
            </span>
          )}
        </GridColumn>

        {isFilterSuggestionsFlagOn && <FilterSuggestions pageType={type} />}
      </GridRow>

      {renderSkipFilters && (
        <SecondaryButton
          data-test="skip-filters-link"
          tag="a"
          href="#main-content"
          className={classNames(styles.visuallyHidden, styles.visibleOnFocus)}
        >
          Skip Filters
        </SecondaryButton>
      )}

      <GridRow id="js-plp-body" className={styles.plp__body}>
        <LeftNavFacets />
        {renderFilterDrawer && (
          <FilterDrawer
            openFilterDrawer={displayFilterDrawer}
            closeFilterDrawer={handleFilterDrawerClose}
            totalNumberOfResults={totalNumberOfResults}
            isPrimaryFacetMainFacet={filterDrawerOpenedByPrimaryFacet}
            onAfterOpen={
              filterDrawerOpenedByPrimaryFacet
                ? handleFilterDrawerOpen
                : undefined
            }
            isMoreFiltersButton={!filterDrawerOpenedByPrimaryFacet}
          />
        )}

        <EnergyDrawer />

        <GridColumn
          colSm={12}
          colMd={12}
          colLg={9}
          colXl={9}
          className={classNames(styles.plp__productGridCol, {
            productListFullWidth: hasProductListError || hasFacetsError,
            [styles['plp__productGridCol--header-peeping']]: isHeaderPeeping,
          })}
        >
          {isHybridBannerFeatureFlagOn && (
            <Banner bannerId="blockBannerId" className="hybridbanners" />
          )}

          <ProductListing
            isHeaderPeeping={isHeaderPeeping}
            handleDisplayM2MProductSelector={handleM2MProductSelectorClick}
            handleDisplayQuickAdd={handleQuickAddDisplay}
            onFetchQuickViewData={onFetchQuickViewData}
            handleFilterDrawerClick={handleFilterDrawerClick}
            handlePrimaryFacetClick={
              isPrimaryFacetInDiscoResponse
                ? handlePrimaryFacetClick
                : undefined
            }
          />

          {page === '1' && (
            <Banner bannerId="seoBottomBannerId" className="seobanners" />
          )}
        </GridColumn>
        <Compare />
        {quickViewData && (
          <QuickViewContainer
            analyticsData={analyticsData}
            fetchEmailOutOfStock={fetchEmailOutOfStock}
            fetchOnlineStockForSkusForSelectedColour={
              fetchOnlineStockForSkusForSelectedColour
            }
            fetchQuickViewData={fetchQuickViewData}
            fetchSocialProofMessagingComponent={
              isSocialProofingQuickViewOn && socialProofMessagesSkeleton
                ? fetchSocialProofMessagesComponentForQuickViewSkeleton
                : undefined
            }
            handleBackButtonClick={handleBackButtonClick}
            hasShortCtaText
            isAnydayEnabled
            isEmailMeFeatureEnabled
            isEnergyRatingEnabled
            isMiniBasketEnabled
            isPromoIndicatorsEnabled
            isUsingCustomerNotifiableEventsData
            isUsingVariantCanonicalUrls
            onCloseQuickView={onCloseQuickView}
            rootId="page-root"
            {...quickViewData}
            atbProps={{
              clientId: 'standard-plp',
              path: basketBFFService,
            }}
            hasNowWasPricing={isNowWasFlagOn}
          />
        )}
        {displayQuickAdd && (
          <QuickAdd
            {...quickAddData}
            analyticsData={analyticsData}
            onRequestClose={handleQuickAddClose}
            fetchEmailOutOfStock={fetchEmailOutOfStock}
            handleColourChangeAction={handleQuickAddColourChange}
            show={displayQuickAdd}
            rootId="page-root"
            error={quickAddError}
            isMiniBasketEnabled
            hasShortCtaText
            isUsingCustomerNotifiableEventsData
            atbProps={{
              clientId: 'standard-plp',
              path: basketBFFService,
            }}
          />
        )}
        {displayM2MProductSelector && (
          <M2MProductSelector
            isOpen={displayM2MProductSelector}
            onClose={onCloseM2MProductSelector}
            pdpUrl={m2mProductSelectorDetails.targetHref}
            rootId="page-root"
          />
        )}
        <BackToTop />
      </GridRow>
    </React.Fragment>
  );
};

export const BannerViewer = memo(
  ({
    breadcrumbs,
    applied,
    isTopBannerFeatureFlagOn,
    isSearchSponsoredBannersFlagOn,
  }) => {
    let props = {};
    const router = useRouter();
    const { type, sortBy, facetId, page = '1' } = router.query;
    const { topBannerId } = useBannerContext();

    const banner = getBrandBanner(breadcrumbs, applied?.facetId);

    const displayCitrusBanner =
      type === 'browse' ||
      (isSearchSponsoredBannersFlagOn &&
        ['s', 'search'].includes(type) &&
        !topBannerId);

    const hideCitrusBanner = !displayCitrusBanner && isTopBannerFeatureFlagOn;

    if (displayCitrusBanner) {
      const facetIdString = facetId
        ? encodeURIComponent(facetId.join('/'))
        : '';
      const pageString = `${facetIdString ? '/' : ''}page/${page}`;
      const sortByString = sortBy
        ? `${facetIdString || pageString ? '/' : ''}sortBy/${sortBy}`
        : '';

      props = {
        isBrandFilterApplied: applied?.isBrandFilterApplied,
        isFilterApplied: Boolean(facetId),
        pathname: `${facetIdString}${pageString}${sortByString}`,
      };
    }

    return (
      <React.Fragment>
        {banner && <BrandBanner banner={banner} />}
        {displayCitrusBanner && <CitrusBanner {...props} />}
        {hideCitrusBanner && (
          <Banner bannerId="topBannerId" className="banners" />
        )}
      </React.Fragment>
    );
  }
);

BannerViewer.displayName = 'BannerViewer';

export const PLP = (props) => {
  /* istanbul ignore next */
  const { isTopBannerFeatureFlagOn, isSearchSponsoredBannersFlagOn } =
    useExperiencesContext();
  const { breadcrumbs } = useBreadcrumbContext();
  const { applied } = useFacetContext();

  return (
    <React.Fragment>
      <BannerViewer
        breadcrumbs={breadcrumbs}
        applied={applied}
        isTopBannerFeatureFlagOn={isTopBannerFeatureFlagOn}
        isSearchSponsoredBannersFlagOn={isSearchSponsoredBannersFlagOn}
      />
      <div className={styles.plp}>
        <PlpView {...props} />
      </div>
    </React.Fragment>
  );
};

PLP.defaultProps = {
  cookies: {},
};

PLP.propTypes = {
  cookies: PropTypes.shape({}),
};

export default PLP;
